import ProjectsApi from "@/api/ProjectsApi";
import router from "@/router";
import store from "@/store";
import { LocalJob } from "@/store/modules/localJobs";

type JobId = string;
type ProjectId = string;
type LocalJobsState = { jobsById: Record<JobId, LocalJob> };
type RootStateWithLocalJobs = AnyRootState & { localJobs?: LocalJobsState };

const POLL_BASE_INTERVAL_MS = 30_000;
const POLL_MAX_INTERVAL_MS = 2 * 60_000;

// A generation that has not reported in this long is not going to, and polling it forever keeps the
// timer alive for nothing. The record stays in the drawer; only the asking stops.
//
// Temporarily seven days rather than one, so jobs already stored with an unrecognised status get
// picked up and resolved. Back to 24 * 60 * 60_000 once those records have drained.
const POLL_MAX_JOB_RECOVERY_AGE_MS = 7 * 24 * 60 * 60_000;

type PlanCreateShape = { activeJobsByProject?: Record<ProjectId, JobId[]> };
type AnyRootState = typeof store.state & { planCreate?: PlanCreateShape };

const ACTIVE_STATUSES = new Set(["PENDING", "SUBMITTED", "INPROGRESS"]);
const FINAL_STATUSES = new Set(["COMPLETED", "FAILED", "STOPPED", "NOTFOUND"]);

// A status outside both sets leaves a job stranded: it is neither polled nor finished, so it sits in
// the drawer forever. Treat it as still running, and never write it over a status we do recognise.
const isKnownStatus = (status: string) => ACTIVE_STATUSES.has(status) || FINAL_STATUSES.has(status);

export type JobStatus = "PENDING" | "SUBMITTED" | "INPROGRESS" | "COMPLETED" | "FAILED" | "STOPPED" | "NOTFOUND";
export type GeneratePlanJob = { status?: JobStatus | string };

function toUpperSafe(v: unknown): string {
  return typeof v === "string" ? v.toUpperCase() : String(v || "");
}

export class JobPollingService {
  private static _instance: JobPollingService | null = null;
  static get instance(): JobPollingService {
    if (!this._instance) this._instance = new JobPollingService();
    return this._instance;
  }

  private api = new ProjectsApi();
  private timer: number | null = null;
  private running = false;
  private interval = POLL_BASE_INTERVAL_MS;
  private unwatch: null | (() => void) = null;

  init() {
    if (this.running) return;
    this.running = true;
    this.unwatch = store.watch(
      (state: AnyRootState) => ({
        activeByProject: state.planCreate?.activeJobsByProject || {},
        localJobs: (state as RootStateWithLocalJobs).localJobs?.jobsById || {},
      }),
      () => {
        this.reconcile();
      }
    );

    this.reconcile();
  }

  destroy() {
    this.stopTimer();
    this.unwatch?.();
    this.unwatch = null;
    this.running = false;
  }

  private buildActiveJobsMap(): Record<ProjectId, JobId[]> {
    const s = store.state as AnyRootState;
    const mapFromPlan = s.planCreate?.activeJobsByProject || {};
    const keys = Object.keys(mapFromPlan || {});
    if (keys.length > 0) {
      return mapFromPlan;
    }

    const localJobs = (s as RootStateWithLocalJobs).localJobs?.jobsById || {};
    const result: Record<ProjectId, JobId[]> = {};
    for (const [jobId, job] of Object.entries<LocalJob>(localJobs)) {
      const status = toUpperSafe(job?.status);
      if (!FINAL_STATUSES.has(status) && !this.isStale(job)) {
        const pid = job?.projectId;
        if (!pid) continue;
        if (!result[pid]) result[pid] = [];
        result[pid].push(jobId as string);
      }
    }
    return result;
  }

  private isStale(job: LocalJob): boolean {
    const started = Date.parse(job?.date ?? "");
    return Number.isFinite(started) && Date.now() - started > POLL_MAX_JOB_RECOVERY_AGE_MS;
  }

  private async tick() {
    try {
      const activeByProject = this.buildActiveJobsMap();
      const projects = Object.keys(activeByProject);

      if (projects.length === 0) {
        this.stopTimer();
        return;
      }

      for (const projectId of projects) {
        const jobIds = activeByProject[projectId] || [];
        for (const jobId of jobIds) {
          await this.pollSingleJob(projectId, jobId);
        }
      }

      this.interval = POLL_BASE_INTERVAL_MS;
    } catch (e) {
      this.interval = Math.min(POLL_MAX_INTERVAL_MS, this.interval * 1.5);
    } finally {
      this.scheduleNext();
    }
  }

  private async pollSingleJob(projectId: string, jobId: string) {
    try {
      let httpStatus: number | undefined;
      const job = await this.api.getProjectGenerateJobById(jobId, (code) => (httpStatus = code));

      // The server no longer knows this job, so no amount of asking will move it.
      if (httpStatus === 404) {
        store.commit("localJobs/updateStatus", { jobId, status: "NOTFOUND" });
        return;
      }

      const status = toUpperSafe(job?.status);

      if (!isKnownStatus(status)) return;

      store.commit("localJobs/updateStatus", { jobId, status });

      if (FINAL_STATUSES.has(status)) {
        if (status === "COMPLETED") {
          const route = router.currentRoute;
          const currentProjectId = route?.params?.projectId;

          if (currentProjectId === projectId && route?.name === "project") {
            await router.replace({
              name: "project",
              params: { projectId },
              query: {
                ...(route.query || {}),
                reload: Date.now().toString(),
              },
            });
          }
          store.commit(
            "app/showAlert",
            { message: "Scenarios processing completed.", type: "success" },
            { root: true }
          );
        } else if (status === "FAILED") {
          store.commit("app/showAlert", { message: "Scenarios processing failed.", type: "error" }, { root: true });
        }
      }
    } catch (err) {
      console.warn(`[JobPolling] job ${jobId} status fetch failed`, err);
    }
  }

  private scheduleNext() {
    this.stopTimer();
    const wait = this.interval;
    this.timer = window.setTimeout(() => this.tick(), wait);
  }

  private stopTimer() {
    if (this.timer != null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private reconcile() {
    const activeByProject = this.buildActiveJobsMap();
    const hasActive = Object.keys(activeByProject).length > 0;

    if (hasActive && this.timer == null) {
      this.interval = POLL_BASE_INTERVAL_MS;
      this.scheduleNext();
    }
    if (!hasActive) {
      this.stopTimer();
    }
  }
}
